import { AvailableRoomModel, AvailableRoomPlan, AvailableRoomType, RoomCancellationPolicy } from '@/__generated';
import { appDisplayDateFormat } from '@/config';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { scrollToElementById } from '@/helpers/utils';
import { clearAndReloadBookmarkHotelsData } from '@/hooks/dataProvider';
import { apiSdk } from '@/httpClient';
import { ISelectedRoomTypes } from '@/pages/HotelDetail/type';
import { add, sumBy } from 'lodash';
import moment from 'moment';

const SEARCH_BOX_SEARCH_KEY = 'SEARCH_BOX_SEARCH_KEY';

class HotelService {
  saveRecentSearchInfo = (name: string, code: string, type: number) => {
    try {
      let recentSearch = this.getRecentSearchInfo() || [];
      recentSearch = recentSearch.filter(x => x.code !== code);
      recentSearch = [{ name: name, type: type, code: code }, ...recentSearch];
      recentSearch = recentSearch.slice(0, 10);
      localStorage.setItem(SEARCH_BOX_SEARCH_KEY, JSON.stringify(recentSearch));
    } catch (error) {}
  };
  getRecentSearchInfo = (): { name: string; type: number; code: string }[] => {
    try {
      let data = localStorage.getItem(SEARCH_BOX_SEARCH_KEY);
      return JSON.parse(data || '')?.map(e => ({ name: e.name, code: e.code, type: parseInt(e.type) })) || [];
    } catch (error) {
      return [];
    }
  };
  bookmark = (code: string, isBookmark: boolean) => {
    let api = isBookmark ? apiSdk.HotelBookmarkService.add({ hotelCode: code }) : apiSdk.HotelBookmarkService.remove({ hotelCode: code });
    return new Promise(resolve => {
      api
        .then()
        .catch()
        .finally(() => {
          clearAndReloadBookmarkHotelsData().finally(() => {
            return resolve(true);
          });
        });
    });
  };
  getCancellationPolicyDate = (dayPeriod: number, checkinDate: string) => {
    if (dayPeriod === -1) {
      return 'After check-in date';
    }
    if (dayPeriod === 0) {
      return `${appDisplayDateFormat.default.withJstDateOnly(moment(checkinDate).add(12, 'h'))} (Check-in date)`;
    }
    return appDisplayDateFormat.default.withJstDateOnly(
      moment(checkinDate)
        .add(12, 'h')
        .add(0 - dayPeriod, 'd'),
    );
  };

  getDefaultCancellationPolicyText = (
    cancellationPolicy: RoomCancellationPolicy | undefined | null,
    checkinDate: string,
    formatCurrency: (price: any) => string,
  ) => {
    if (cancellationPolicy) {
      if (!cancellationPolicy.value) {
        let dateString = appDisplayDateFormat.default.withJstDateOnly(
          moment(checkinDate)
            .add(12, 'h')
            .add(1, 'day')
            .add(0 - (cancellationPolicy.dayPeriod || 0), 'd'),
        );

        return `Free cancellation before ${dateString}`;
      } else {
        if (cancellationPolicy.isPercentage) {
          return `${cancellationPolicy.value}% of Hotel fee`;
        } else {
          return `Cancellation fee :${formatCurrency(cancellationPolicy.value)}`;
        }
      }
    }
    return '';
  };
  getCancellationPolicyText = (
    cancellationPolicy: RoomCancellationPolicy | undefined | null,
    checkinDate: string,
    formatCurrency: (price: any) => string,
  ) => {
    if (cancellationPolicy) {
      if (!cancellationPolicy.value) {
        return `Free cancellation`;
      } else {
        if (cancellationPolicy.isPercentage) {
          return `${cancellationPolicy.value}% of Hotel fee`;
        } else {
          return `Cancellation fee :${formatCurrency(cancellationPolicy.value)}`;
        }
      }
    }
    return '';
  };

  createSelectedRooms = (
    noOfAdult: number,
    noOfChild: number,
    roomType: AvailableRoomType,
    plan: AvailableRoomPlan,
    selectedRoomNumber: number,
    selectedRoomsClone: ISelectedRoomTypes[],
  ) => {
    let availableRooms = selectedRoomsClone.find(x => x.noOfAdult === noOfAdult && x.noOfChild === noOfChild);
    if (availableRooms) {
      let existRoomTypes = availableRooms.roomTypes.find(
        x =>
          x.category === roomType.category &&
          x.roomTypeCode === roomType.roomTypeCode &&
          x.roomTypeId === roomType.roomTypeId &&
          x.supplier === roomType.supplier,
      );
      if (existRoomTypes) {
        existRoomTypes.plans = existRoomTypes.plans.filter(x => x.code !== plan.code);
        if (selectedRoomNumber) {
          existRoomTypes.plans.push({
            boardType: plan.boardType,
            code: plan.code,
            currency: plan.currency,
            name: plan.name,
            price: plan.price || 0,
            selectedRooms: selectedRoomNumber,
            roomPlanId: plan.planId || '',
          });
        }
      } else {
        availableRooms.roomTypes.push({
          category: roomType.category,
          name: roomType.name,
          roomTypeCode: roomType.roomTypeCode,
          supplier: roomType.supplier,
          roomTypeId: roomType.roomTypeId,
          plans: [
            {
              boardType: plan.boardType,
              code: plan.code,
              currency: plan.currency,
              name: plan.name,
              price: plan.price || 0,
              selectedRooms: selectedRoomNumber,
              roomPlanId: plan.planId || '',
            },
          ],
        });
      }
    } else {
      selectedRoomsClone.push({
        noOfAdult: noOfAdult,
        noOfChild: noOfChild,
        roomTypes: [
          {
            category: roomType.category,
            name: roomType.name,
            roomTypeCode: roomType.roomTypeCode,
            supplier: roomType.supplier,
            roomTypeId: roomType.roomTypeId,
            plans: [
              {
                boardType: plan.boardType,
                code: plan.code,
                currency: plan.currency,
                name: plan.name,
                price: plan.price || 0,
                selectedRooms: selectedRoomNumber,
                roomPlanId: plan.planId || '',
              },
            ],
          },
        ],
      });
    }
    return selectedRoomsClone;
  };

  getTotalSelectedRoomWithRoomTypeId = (selectedRooms: ISelectedRoomTypes[], roomTypeId: string) => {
    let total = 0;
    selectedRooms.forEach(room => {
      room.roomTypes.forEach(roomType => {
        if (roomType.roomTypeId === roomTypeId) {
          total += sumBy(roomType.plans, x => x.selectedRooms);
        }
      });
    });
    return total;
  };

  checkSelectedRoomsBeforeSubmit = (selectedRooms: ISelectedRoomTypes[], availableRooms: AvailableRoomModel[]): Promise<boolean> => {
    return new Promise(resolve => {
      if (availableRooms && selectedRooms) {
        // Verify if user is booking more or fewer rooms than the rooms in search
        for (var i = 0; i < availableRooms.length; i++) {
          let availableRoom = availableRooms[i];
          let selectedRoom = selectedRooms.find(x => x.noOfAdult === availableRoom.noOfAdult && x.noOfChild === availableRoom.noOfChild);
          let totalSelectedRooms = 0;
          selectedRoom?.roomTypes.forEach(room => {
            totalSelectedRooms += sumBy(room.plans, x => x.selectedRooms);
          });
          if (totalSelectedRooms !== availableRoom.noOfRooms) {
            ModalUtils.error({
              title: 'Error',
              content: 'You have selected more or fewer rooms than indicated in your search. Please adjust the number of rooms.',
              onClose: () => {
                scrollToElementById(null, `room-category-${availableRoom.noOfAdult}-${availableRoom.noOfChild}`);
              },
            });
            return resolve(false);
          }
        }

        // Verify if any room exceeds the allotments
        for (var i = 0; i < availableRooms.length; i++) {
          let { availableRoomTypes = [], noOfAdult, noOfChild } = availableRooms[i];
          for (var y = 0; y < availableRoomTypes?.length; y++) {
            let { availableSlot = 0, roomTypeId } = availableRoomTypes[y];
            if (availableSlot < this.getTotalSelectedRoomWithRoomTypeId(selectedRooms, roomTypeId || '')) {
              ModalUtils.error({
                title: 'Error',
                content: 'The number of selected rooms exceeds the allotted limit. Please review your selection.',
                onClose: () => {
                  scrollToElementById(null, `${noOfAdult}-${noOfChild}-room-${roomTypeId}`);
                },
              });

              return resolve(false);
            }
          }
        }
        // Check if the selected rooms belong to more than one rate plan:
        let ratePlanCodes = {};
        selectedRooms.forEach(rooms => {
          rooms.roomTypes.forEach(roomType => {
            roomType.plans.forEach(plan => {
              ratePlanCodes[plan.roomPlanId] = true;
            });
          });
        });
        if (Object.keys(ratePlanCodes).length !== 1) {
          ModalUtils.confirm({
            title: 'You have selected rooms with different rate plans',
            content: `You have selected rooms with different rate plans. 
            There is a possibility that your booking will be partially confirmed, 
            and the cancellation charge will be applied according to the cancellation policy if you decide to cancel the partial booking`,
            onConfirm: () => {
              return resolve(true);
            },
            onClose: () => {
              return resolve(false);
            },
            okText: 'Proceed',
          });
        } else {
          return resolve(true);
        }
      } else {
        return resolve(false);
      }
    });
  };
}

export default new HotelService();
